import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import PropertyManagersMap from './PropertyManagersMap';
import axios from 'axios';
import { API } from '../utils/config';
import {
  UserCheck,
  Plus,
  Search,
  Edit2,
  Trash2,
  Building,
  User,
  CheckCircle,
  AlertCircle,
  Home,
  MapPin,
  Map,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Mail,
  Send,
  Loader2,
  Clock,
  CheckCircle2,
  RefreshCw,
  Users,
  Eye,
  CreditCard,
  Building2,
  X,
  LogIn
} from 'lucide-react';


const PropertyManagersManager = () => {
  const [managers, setManagers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewManager, setShowNewManager] = useState(false);
  const [showEditManager, setShowEditManager] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMap, setShowMap] = useState(false);
  
  // Invitation states
  const [invitationStats, setInvitationStats] = useState(null);
  const [sendingInvitation, setSendingInvitation] = useState(null);
  const [showBulkInviteDialog, setShowBulkInviteDialog] = useState(false);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  // Email Test diagnostic (admin only)
  const [showEmailTestDialog, setShowEmailTestDialog] = useState(false);
  const [emailTestTo, setEmailTestTo] = useState('');
  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailManager, setDetailManager] = useState(null);
  
  const [newManager, setNewManager] = useState({
    first_name: '',
    last_name: '',
    entity_type: 'private',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    country: 'Italia',
    operation_area_city: '',
    operation_area_province: '',
    is_also_partner: false,
    notes: '',
    // Dati Bancari
    fiscal_code: '',
    bank_name: '',
    account_holder: '',
    iban: '',
    bic_swift: ''
  });

  useEffect(() => {
    fetchManagers();
    fetchProperties();
    fetchInvitationStats();
  }, []);

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/property-managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManagers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching managers:', error);
      showMessage('error', 'Errore nel caricamento dei responsabili');
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchInvitationStats = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/property-managers/invitation-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitationStats(response.data);
    } catch (error) {
      console.error('Error fetching invitation stats:', error);
    }
  };

  const handleSendInvitation = async (managerId) => {
    setSendingInvitation(managerId);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/property-managers/${managerId}/send-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mostra messaggio principale + eventuali warning (es. WhatsApp non configurato)
      showMessage('success', response.data.message);
      if (response.data.warnings && response.data.warnings.length > 0) {
        // Mostra warning come messaggi separati dopo 1.5s ciascuno
        response.data.warnings.forEach((w, i) => {
          setTimeout(() => showMessage('warning', w), (i + 1) * 1500);
        });
      }
      fetchManagers();
      fetchInvitationStats();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Errore nell\'invio dell\'invito');
    } finally {
      setSendingInvitation(null);
    }
  };

  const handleBulkInvite = async () => {
    if (selectedForBulk.length === 0) {
      showMessage('error', 'Seleziona almeno un responsabile');
      return;
    }
    
    setBulkSending(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/api/property-managers/send-invitations-bulk`,
        { manager_ids: selectedForBulk },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage('success', response.data.message);
      setShowBulkInviteDialog(false);
      setSelectedForBulk([]);
      fetchManagers();
      fetchInvitationStats();
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Errore nell\'invio bulk');
    } finally {
      setBulkSending(false);
    }
  };

  const toggleBulkSelection = (managerId) => {
    setSelectedForBulk(prev => 
      prev.includes(managerId) 
        ? prev.filter(id => id !== managerId)
        : [...prev, managerId]
    );
  };

  const selectAllForBulk = () => {
    const eligibleManagers = managers.filter(m => m.email && !m.account_activated);
    setSelectedForBulk(eligibleManagers.map(m => m.id));
  };

  const getInvitationStatus = (manager) => {
    // Check if activated via account_activated OR invitation_status
    if (manager.account_activated || manager.invitation_status === 'registered') {
      return { status: 'activated', label: 'Attivato', color: 'bg-green-100 text-green-700' };
    }
    if (manager.invitation_sent_at) {
      return { status: 'pending', label: 'In attesa', color: 'bg-yellow-100 text-yellow-700' };
    }
    if (manager.email) {
      return { status: 'not_sent', label: 'Non invitato', color: 'bg-slate-100 text-slate-600' };
    }
    return { status: 'no_email', label: 'No email', color: 'bg-red-100 text-red-700' };
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/api/property-managers`, newManager, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showMessage('success', 'Responsabile creato con successo');
      setShowNewManager(false);
      resetForm();
      fetchManagers();
    } catch (error) {
      console.error('Error creating manager:', error);
      showMessage('error', error.response?.data?.detail || 'Errore nella creazione del responsabile');
    }
  };

  const handleUpdateManager = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      console.log('Updating manager:', selectedManager.id, newManager);
      
      const response = await axios.put(`${API}/api/property-managers/${selectedManager.id}`, newManager, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Update response:', response.data);
      toast.success('Responsabile aggiornato con successo!');
      setShowEditManager(false);
      setSelectedManager(null);
      resetForm();
      fetchManagers();
    } catch (error) {
      console.error('Error updating manager:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento del responsabile');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteManager = async (managerId, force = false) => {
    const confirmMsg = force 
      ? 'ATTENZIONE: Stai per eliminare questo responsabile E rimuovere tutte le associazioni con gli immobili. Continuare?'
      : 'Sei sicuro di voler eliminare questo responsabile?';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      const url = force 
        ? `${API}/api/property-managers/${managerId}?force=true`
        : `${API}/api/property-managers/${managerId}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Responsabile eliminato con successo');
      fetchManagers();
    } catch (error) {
      console.error('Error deleting manager:', error);
      const errorMsg = error.response?.data?.detail || 'Errore nell\'eliminazione del responsabile';
      
      // Check if error is about associated properties
      if (errorMsg.includes('gestisce') && errorMsg.includes('immobili')) {
        // Ask if user wants to force delete
        if (window.confirm(`${errorMsg}\n\nVuoi eliminare comunque il responsabile e rimuovere le associazioni con gli immobili?`)) {
          handleDeleteManager(managerId, true);
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Impersona un responsabile
  const handleImpersonate = async (manager) => {
    try {
      const token = localStorage.getItem('hw_token');
      
      // Usa il nuovo endpoint che crea l'account se non esiste
      const response = await axios.post(`${API}/api/admin/impersonate-manager`, 
        { manager_id: manager.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.access_token) {
        // Salva il token admin originale per poter tornare
        localStorage.setItem('hw_admin_token', token);
        localStorage.setItem('hw_impersonating', 'true');
        localStorage.setItem('hw_impersonated_user', `${manager.first_name || ''} ${manager.last_name || ''}`.trim() || manager.email);
        
        // Imposta il nuovo token
        localStorage.setItem('hw_token', response.data.access_token);
        
        toast.success(`Stai impersonando: ${manager.first_name || ''} ${manager.last_name || ''}`);
        
        // Reindirizza alla dashboard del responsabile
        window.location.href = '/responsabile/dashboard';
      }
    } catch (error) {
      console.error('Error impersonating:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'impersonation');
    }
  };

  const openEditDialog = (manager) => {
    setSelectedManager(manager);
    setNewManager({
      first_name: manager.first_name,
      last_name: manager.last_name,
      entity_type: manager.entity_type,
      phone: manager.phone,
      email: manager.email,
      address: manager.address,
      city: manager.city,
      province: manager.province,
      country: manager.country,
      operation_area_city: manager.operation_area_city || '',
      operation_area_province: manager.operation_area_province || '',
      is_also_partner: manager.is_also_partner,
      notes: manager.notes || '',
      // Dati Bancari
      fiscal_code: manager.fiscal_code || '',
      bank_name: manager.bank_name || '',
      account_holder: manager.account_holder || '',
      iban: manager.iban || '',
      bic_swift: manager.bic_swift || ''
    });
    setShowEditManager(true);
  };

  const resetForm = () => {
    setNewManager({
      first_name: '',
      last_name: '',
      entity_type: 'private',
      phone: '',
      email: '',
      address: '',
      city: '',
      province: '',
      country: 'Italia',
      operation_area_city: '',
      operation_area_province: '',
      is_also_partner: false,
      notes: '',
      // Dati Bancari
      fiscal_code: '',
      bank_name: '',
      account_holder: '',
      iban: '',
      bic_swift: ''
    });
  };

  const getManagerProperties = (managerId) => {
    // Return only NON-ARCHIVED properties
    return properties.filter(p => p.property_manager_id === managerId && !p.archived);
  };
  
  // Get archived properties for a manager (for info purposes)
  const getManagerArchivedProperties = (managerId) => {
    return properties.filter(p => p.property_manager_id === managerId && p.archived);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" />
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  const filteredManagers = managers
    .filter(manager => {
      const fullName = `${manager.first_name} ${manager.last_name}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                           manager.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (manager.operation_area_city && manager.operation_area_city.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = entityTypeFilter === 'all' || manager.entity_type === entityTypeFilter;
      const matchesCity = !cityFilter || (manager.operation_area_city && manager.operation_area_city.toLowerCase().includes(cityFilter.toLowerCase()));
      const matchesPartner = partnerFilter === 'all' || 
                            (partnerFilter === 'yes' && manager.is_also_partner) ||
                            (partnerFilter === 'no' && !manager.is_also_partner);
      const matchesCompleteness = completenessFilter === 'all' || 
                                 (completenessFilter === 'complete' && !manager.is_incomplete) ||
                                 (completenessFilter === 'incomplete' && manager.is_incomplete);
      return matchesSearch && matchesType && matchesCity && matchesPartner && matchesCompleteness;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch(sortField) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'city':
          aValue = a.operation_area_city || '';
          bValue = b.operation_area_city || '';
          break;
        case 'properties':
          aValue = getManagerProperties(a.id).length;
          bValue = getManagerProperties(b.id).length;
          break;
        case 'invitation_status':
          // Ordinamento: activated > pending > not_sent > no_email
          const getStatusOrder = (m) => {
            if (m.account_activated || m.invitation_status === 'registered') return 1;
            if (m.invitation_sent_at) return 2;
            if (m.email) return 3;
            return 4;
          };
          aValue = getStatusOrder(a);
          bValue = getStatusOrder(b);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) {
    return (
      <Layout title="Responsabili Immobili">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Caricamento responsabili...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Responsabili Immobili">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <UserCheck className="h-8 w-8 text-blue-900" />
              Gestione Responsabili
              <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 text-lg font-semibold rounded-full">
                {managers.length}
              </span>
            </h1>
            <p className="text-slate-600 mt-2">
              Responsabili che gestiscono operativamente gli immobili
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowMap(true)}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Map className="h-4 w-4 mr-2" />
              Mappa
            </Button>
            <Button
              onClick={() => setShowNewManager(true)}
              className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Responsabile
            </Button>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200'
              : message.type === 'warning'
              ? 'bg-amber-50 text-amber-900 border border-amber-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : message.type === 'warning' ? (
              <AlertCircle className="h-5 w-5 text-amber-600" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            {message.text}
          </div>
        )}

        {/* Invitation Stats Card */}
        {invitationStats && (
          <Card className="p-4 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-slate-700">Stato Inviti:</span>
                </div>
                <div className="flex gap-4">
                  <div className="text-center px-3 py-1 bg-green-100 rounded-lg">
                    <span className="text-lg font-bold text-green-700">{invitationStats.activated}</span>
                    <p className="text-xs text-green-600">Attivati</p>
                  </div>
                  <div className="text-center px-3 py-1 bg-yellow-100 rounded-lg">
                    <span className="text-lg font-bold text-yellow-700">{invitationStats.pending_activation}</span>
                    <p className="text-xs text-yellow-600">In attesa</p>
                  </div>
                  <div className="text-center px-3 py-1 bg-slate-100 rounded-lg">
                    <span className="text-lg font-bold text-slate-700">{invitationStats.never_invited}</span>
                    <p className="text-xs text-slate-600">Mai invitati</p>
                  </div>
                  <div className="text-center px-3 py-1 bg-red-100 rounded-lg">
                    <span className="text-lg font-bold text-red-700">{invitationStats.without_email}</span>
                    <p className="text-xs text-red-600">Senza email</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setShowBulkInviteDialog(true)}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-100"
              >
                <Send className="h-4 w-4 mr-2" />
                Invio Bulk
              </Button>
              <Button
                onClick={() => setShowEmailTestDialog(true)}
                variant="outline"
                className="border-amber-600 text-amber-700 hover:bg-amber-100 ml-2"
                data-testid="open-email-test-btn"
                title="Verifica perché un'email specifica non arriva (es. domain reject, mailbox piena)"
              >
                <AlertCircle className="h-4 w-4 mr-2" />
                Test Email
              </Button>
            </div>
          </Card>
        )}

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Cerca per nome, email o zona..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div>
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo entità" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="private">Privati</SelectItem>
                  <SelectItem value="company">Aziende</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Filtra per zona operativa..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="pl-10"
              />
            </div>
            <div>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Anche partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="yes">Anche partner</SelectItem>
                  <SelectItem value="no">Solo responsabili</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={completenessFilter} onValueChange={setCompletenessFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Stato dati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="complete">✅ Completi</SelectItem>
                  <SelectItem value="incomplete">⚠️ Incompleti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Managers Table */}
        <Card className="overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200">
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Responsabile
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Tipo
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Contatti
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Residenza
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('city')}
                  >
                    <div className="flex items-center gap-2">
                      Zona Operativa
                      {getSortIcon('city')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('properties')}
                  >
                    <div className="flex items-center gap-2">
                      Immobili Gestiti
                      {getSortIcon('properties')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                    Anche Partner
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                    onClick={() => handleSort('invitation_status')}
                  >
                    <div className="flex items-center gap-2">
                      Stato Account
                      {getSortIcon('invitation_status')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">
                    Dati Bancari
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredManagers.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-12 text-center text-slate-500">
                      <UserCheck className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-lg font-medium">Nessun responsabile trovato</p>
                      <p className="text-sm mt-2">Aggiungi il tuo primo responsabile</p>
                    </td>
                  </tr>
                ) : (
                  filteredManagers.map((manager) => {
                    const managedProps = getManagerProperties(manager.id);
                    const isIncomplete = manager.is_incomplete || false;
                    const invStatus = getInvitationStatus(manager);
                    return (
                      <tr 
                        key={manager.id} 
                        className={`hover:bg-slate-50 transition-colors ${
                          isIncomplete ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {manager.entity_type === 'company' ? (
                              <Building className="h-5 w-5 text-blue-600" />
                            ) : (
                              <User className="h-5 w-5 text-green-600" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">
                                  {manager.first_name} {manager.last_name}
                                </p>
                                {isIncomplete && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                    <AlertCircle className="h-3 w-3" />
                                    Dati Mancanti
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{manager.email}</p>
                              {isIncomplete && manager.missing_fields && manager.missing_fields.length > 0 && (
                                <p className="text-xs text-red-600 mt-1">
                                  Mancanti: {manager.missing_fields.slice(0, 3).join(', ')}
                                  {manager.missing_fields.length > 3 && ` +${manager.missing_fields.length - 3}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            manager.entity_type === 'company'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {manager.entity_type === 'company' ? 'Azienda' : 'Privato'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          <p>{manager.phone}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          <p>{manager.city}, {manager.province}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {manager.operation_area_city ? (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4 text-orange-600" />
                              <span>{manager.operation_area_city}, {manager.operation_area_province}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">Non specificata</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Home className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold text-blue-900">{managedProps.length}</span>
                            {managedProps.length > 0 && (
                              <span className="text-xs text-slate-500">immobili</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {manager.is_also_partner && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                              <CheckCircle className="h-3 w-3" />
                              Sì
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Badge className={invStatus.color}>
                              {invStatus.status === 'activated' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {invStatus.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {invStatus.label}
                            </Badge>
                            {invStatus.status !== 'activated' && invStatus.status !== 'no_email' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendInvitation(manager.id)}
                                disabled={sendingInvitation === manager.id}
                                className="text-blue-600 hover:bg-blue-50"
                                title={invStatus.status === 'pending' ? 'Re-invia invito' : 'Invia invito'}
                              >
                                {sendingInvitation === manager.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {manager.iban ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle className="h-3 w-3" />
                              Completi
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                              <AlertCircle className="h-3 w-3" />
                              Mancanti
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDetailManager(manager);
                                setShowDetailModal(true);
                              }}
                              className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                              title="Visualizza dettagli"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleImpersonate(manager)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Impersona questo responsabile"
                            >
                              <LogIn className="h-5 w-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(manager)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteManager(manager.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Bulk Invite Dialog */}
        <Dialog open={showBulkInviteDialog} onOpenChange={setShowBulkInviteDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                Invio Inviti Bulk
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Quick actions */}
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg">
                <span className="text-sm text-slate-600">
                  Selezionati: <strong>{selectedForBulk.length}</strong> responsabili
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllForBulk}>
                    Seleziona Tutti
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedForBulk([])}>
                    Deseleziona
                  </Button>
                </div>
              </div>

              {/* Manager list */}
              <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                {managers.filter(m => m.email && !m.account_activated).length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    <p>Tutti i responsabili con email hanno già attivato l'account!</p>
                  </div>
                ) : (
                  managers
                    .filter(m => m.email && !m.account_activated)
                    .map(manager => (
                      <div 
                        key={manager.id}
                        className="flex items-center justify-between p-3 border-b hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedForBulk.includes(manager.id)}
                            onCheckedChange={() => toggleBulkSelection(manager.id)}
                          />
                          <div>
                            <p className="font-medium">{manager.first_name} {manager.last_name}</p>
                            <p className="text-sm text-slate-500">{manager.email}</p>
                          </div>
                        </div>
                        <Badge className={getInvitationStatus(manager).color}>
                          {getInvitationStatus(manager).label}
                        </Badge>
                      </div>
                    ))
                )}
              </div>

              {/* Send button */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowBulkInviteDialog(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={handleBulkInvite}
                  disabled={bulkSending || selectedForBulk.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {bulkSending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Invia a {selectedForBulk.length} responsabili
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ====================== EMAIL TEST DIAGNOSTIC DIALOG ====================== */}
        <Dialog open={showEmailTestDialog} onOpenChange={(o) => {
          setShowEmailTestDialog(o);
          if (!o) { setEmailTestResult(null); setEmailTestTo(''); }
        }}>
          <DialogContent className="max-w-2xl" data-testid="email-test-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Test Email Diagnostico
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Strumento per diagnosticare perché email specifiche non arrivano <strong>solo ad alcuni utenti</strong>.
                Invia un'email di test e mostra il dettaglio della risposta del server SMTP
                (bounce, blocco dominio, mailbox piena, ecc.).
              </div>
              <div>
                <Label className="text-sm font-medium">Destinatario test</Label>
                <Input
                  type="email"
                  value={emailTestTo}
                  onChange={(e) => setEmailTestTo(e.target.value)}
                  placeholder="es. responsabile@dominio.it"
                  data-testid="email-test-to-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Inserisci l'indirizzo che NON riceve email per scoprire perché.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEmailTestDialog(false)} data-testid="email-test-cancel-btn">
                  Chiudi
                </Button>
                <Button
                  onClick={async () => {
                    if (!emailTestTo || !emailTestTo.includes('@')) {
                      showMessage('error', 'Indirizzo non valido');
                      return;
                    }
                    setEmailTestLoading(true);
                    setEmailTestResult(null);
                    try {
                      const token = localStorage.getItem('hw_token');
                      const r = await axios.post(
                        `${API}/api/admin/email-test`,
                        { to_email: emailTestTo },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setEmailTestResult(r.data);
                    } catch (err) {
                      setEmailTestResult({
                        success: false,
                        error_message: err.response?.data?.detail || err.message || 'Errore richiesta',
                        friendly_message: 'Errore nella chiamata al server diagnostico',
                      });
                    } finally {
                      setEmailTestLoading(false);
                    }
                  }}
                  disabled={emailTestLoading || !emailTestTo}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="email-test-send-btn"
                >
                  {emailTestLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Invio test...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" />Invia test</>
                  )}
                </Button>
              </div>

              {emailTestResult && (
                <div className="mt-2 border rounded-lg overflow-hidden" data-testid="email-test-result">
                  <div className={`px-4 py-2 flex items-center gap-2 font-semibold ${
                    emailTestResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}>
                    {emailTestResult.success ? (
                      <><CheckCircle className="h-4 w-4" /> Email inviata con successo</>
                    ) : (
                      <><AlertCircle className="h-4 w-4" /> Invio fallito</>
                    )}
                  </div>
                  <div className="px-4 py-3 space-y-2 text-sm bg-white">
                    {emailTestResult.friendly_message && (
                      <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                        <div className="font-semibold text-amber-900 mb-1">Spiegazione:</div>
                        <div className="text-amber-900">{emailTestResult.friendly_message}</div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-slate-500">SMTP host:</span> <code>{emailTestResult.smtp_host}:{emailTestResult.smtp_port}</code></div>
                      <div><span className="text-slate-500">From:</span> <code>{emailTestResult.smtp_from}</code></div>
                      <div><span className="text-slate-500">To:</span> <code>{emailTestResult.to_email}</code></div>
                      <div><span className="text-slate-500">Durata:</span> {emailTestResult.duration_ms}ms</div>
                      {emailTestResult.error_class && (
                        <div className="col-span-2"><span className="text-slate-500">Eccezione:</span> <code>{emailTestResult.error_class}</code></div>
                      )}
                      {emailTestResult.error_code && (
                        <div className="col-span-2"><span className="text-slate-500">SMTP code:</span> <code>{emailTestResult.error_code}</code></div>
                      )}
                      {emailTestResult.error_message && (
                        <div className="col-span-2"><span className="text-slate-500">Messaggio raw:</span> <code className="break-all">{emailTestResult.error_message}</code></div>
                      )}
                      {emailTestResult.smtp_response && (
                        <div className="col-span-2"><span className="text-slate-500">Risposta server:</span> <code>{emailTestResult.smtp_response}</code></div>
                      )}
                      {emailTestResult.smtp_refused && Object.keys(emailTestResult.smtp_refused).length > 0 && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Destinatari rifiutati:</span>
                          <pre className="text-xs bg-slate-50 p-2 rounded mt-1 whitespace-pre-wrap">{JSON.stringify(emailTestResult.smtp_refused, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                    {emailTestResult.traceback_short && (
                      <details className="mt-2">
                        <summary className="text-xs cursor-pointer text-slate-500 hover:text-slate-700">Dettagli tecnici (traceback)</summary>
                        <pre className="text-xs bg-slate-50 p-2 rounded mt-1 whitespace-pre-wrap max-h-48 overflow-auto">{emailTestResult.traceback_short}</pre>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Manager Dialog */}
        {(showNewManager || showEditManager) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {showEditManager ? 'Modifica Responsabile' : 'Nuovo Responsabile'}
              </h2>
              <form onSubmit={showEditManager ? handleUpdateManager : handleCreateManager} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      type="text"
                      value={newManager.first_name}
                      onChange={(e) => setNewManager({...newManager, first_name: e.target.value})}
                      placeholder="Mario"
                      required
                    />
                  </div>
                  <div>
                    <Label>Cognome *</Label>
                    <Input
                      type="text"
                      value={newManager.last_name}
                      onChange={(e) => setNewManager({...newManager, last_name: e.target.value})}
                      placeholder="Rossi"
                      required
                    />
                  </div>
                  <div>
                    <Label>Tipo *</Label>
                    <Select 
                      value={newManager.entity_type}
                      onValueChange={(value) => setNewManager({...newManager, entity_type: value})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Privato</SelectItem>
                        <SelectItem value="company">Azienda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Telefono *</Label>
                    <Input
                      type="tel"
                      value={newManager.phone}
                      onChange={(e) => setNewManager({...newManager, phone: e.target.value})}
                      placeholder="+39 123 456 7890"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newManager.email}
                      onChange={(e) => setNewManager({...newManager, email: e.target.value})}
                      placeholder="responsabile@example.com"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Indirizzo (opzionale)</Label>
                    <Input
                      type="text"
                      value={newManager.address}
                      onChange={(e) => setNewManager({...newManager, address: e.target.value})}
                      placeholder="Via Roma, 123"
                    />
                    <p className="text-xs text-slate-500 mt-1">Può essere inserito dal responsabile nell'area riservata</p>
                  </div>
                  <div>
                    <Label>Città (opzionale)</Label>
                    <Input
                      type="text"
                      value={newManager.city}
                      onChange={(e) => setNewManager({...newManager, city: e.target.value})}
                      placeholder="Roma"
                    />
                  </div>
                  <div>
                    <Label>Provincia (opzionale)</Label>
                    <Input
                      type="text"
                      value={newManager.province}
                      onChange={(e) => setNewManager({...newManager, province: e.target.value})}
                      placeholder="RM"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Paese (opzionale)</Label>
                    <Input
                      type="text"
                      value={newManager.country}
                      onChange={(e) => setNewManager({...newManager, country: e.target.value})}
                      placeholder="Italia"
                    />
                  </div>
                  
                  {/* Zona Operativa */}
                  <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-orange-600" />
                      Zona Operativa
                    </h3>
                  </div>
                  <div>
                    <Label>Comune</Label>
                    <Input
                      type="text"
                      value={newManager.operation_area_city}
                      onChange={(e) => setNewManager({...newManager, operation_area_city: e.target.value})}
                      placeholder="Milano"
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      type="text"
                      value={newManager.operation_area_province}
                      onChange={(e) => setNewManager({...newManager, operation_area_province: e.target.value})}
                      placeholder="MI"
                    />
                  </div>
                  
                  {/* Dati Bancari */}
                  <div className="md:col-span-2 mt-4">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-green-600" />
                      Coordinate Bancarie
                    </h3>
                    <p className="text-xs text-slate-500 mb-3">Dati per il pagamento dei compensi</p>
                  </div>
                  <div>
                    <Label>Codice Fiscale</Label>
                    <Input
                      type="text"
                      value={newManager.fiscal_code}
                      onChange={(e) => setNewManager({...newManager, fiscal_code: e.target.value.toUpperCase()})}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                    />
                  </div>
                  <div>
                    <Label>Nome Banca</Label>
                    <Input
                      type="text"
                      value={newManager.bank_name}
                      onChange={(e) => setNewManager({...newManager, bank_name: e.target.value})}
                      placeholder="Intesa Sanpaolo"
                    />
                  </div>
                  <div>
                    <Label>Intestatario Conto</Label>
                    <Input
                      type="text"
                      value={newManager.account_holder}
                      onChange={(e) => setNewManager({...newManager, account_holder: e.target.value})}
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div>
                    <Label>BIC/SWIFT</Label>
                    <Input
                      type="text"
                      value={newManager.bic_swift}
                      onChange={(e) => setNewManager({...newManager, bic_swift: e.target.value.toUpperCase()})}
                      placeholder="BCITITMM"
                      maxLength={11}
                      className="font-mono"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>IBAN</Label>
                    <Input
                      type="text"
                      value={newManager.iban}
                      onChange={(e) => {
                        const clean = e.target.value.replace(/\s/g, '').toUpperCase();
                        const formatted = clean.replace(/(.{4})/g, '$1 ').trim();
                        setNewManager({...newManager, iban: formatted});
                      }}
                      placeholder="IT60 X054 2811 1010 0000 0123 456"
                      className="font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">L'IBAN italiano inizia con IT e ha 27 caratteri</p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label>Note</Label>
                    <textarea
                      value={newManager.notes}
                      onChange={(e) => setNewManager({...newManager, notes: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows="3"
                      placeholder="Note aggiuntive..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_also_partner"
                        checked={newManager.is_also_partner}
                        onChange={(e) => setNewManager({...newManager, is_also_partner: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_also_partner" className="text-sm font-medium text-slate-700">
                        ✅ È anche Partner
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      Se selezionato, verrà creato automaticamente anche come Partner
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowNewManager(false);
                      setShowEditManager(false);
                      setSelectedManager(null);
                      resetForm();
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {showEditManager ? 'Aggiorna' : 'Crea Responsabile'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}
      </div>

      {/* Map Dialog */}
      <PropertyManagersMap 
        open={showMap} 
        onClose={() => setShowMap(false)} 
      />

      {/* Detail Modal */}
      {showDetailModal && detailManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-800">Dettagli Responsabile</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dati Personali */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2">
                    <User className="h-4 w-4 text-blue-600" /> Dati Personali
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Nome:</span> <strong>{detailManager.first_name} {detailManager.last_name}</strong></p>
                    <p><span className="text-slate-500">Email:</span> <strong>{detailManager.email || '-'}</strong></p>
                    <p><span className="text-slate-500">Telefono:</span> <strong>{detailManager.phone || '-'}</strong></p>
                    <p><span className="text-slate-500">Codice Fiscale:</span> <strong>{detailManager.fiscal_code || '-'}</strong></p>
                    <p><span className="text-slate-500">Indirizzo:</span> <strong>{detailManager.address || '-'}</strong></p>
                    <p><span className="text-slate-500">Città:</span> <strong>{detailManager.city}{detailManager.province ? `, ${detailManager.province}` : ''}</strong></p>
                  </div>
                  
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2 mt-6">
                    <MapPin className="h-4 w-4 text-orange-600" /> Zona Operativa
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Comune:</span> <strong>{detailManager.operation_area_city || '-'}</strong></p>
                    <p><span className="text-slate-500">Provincia:</span> <strong>{detailManager.operation_area_province || '-'}</strong></p>
                  </div>
                </div>

                {/* Dati Bancari */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2">
                    <CreditCard className="h-4 w-4 text-green-600" /> Coordinate Bancarie
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Banca:</span> <strong>{detailManager.bank_name || '-'}</strong></p>
                    <p><span className="text-slate-500">Intestatario:</span> <strong>{detailManager.account_holder || '-'}</strong></p>
                    <p><span className="text-slate-500">IBAN:</span> <strong className="font-mono text-xs">{detailManager.iban || '-'}</strong></p>
                    <p><span className="text-slate-500">BIC/SWIFT:</span> <strong className="font-mono">{detailManager.bic_swift || '-'}</strong></p>
                  </div>
                  
                  {detailManager.iban && detailManager.bic_swift ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 text-sm mt-4">
                      <CheckCircle2 className="h-4 w-4" />
                      Coordinate bancarie complete
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg text-orange-700 text-sm mt-4">
                      <AlertCircle className="h-4 w-4" />
                      Coordinate bancarie incomplete
                    </div>
                  )}
                  
                  {/* Immobili gestiti */}
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 border-b pb-2 mt-6">
                    <Home className="h-4 w-4 text-blue-600" /> Immobili Gestiti
                  </h3>
                  <div className="text-sm">
                    {(() => {
                      const props = getManagerProperties(detailManager.id);
                      if (props.length === 0) {
                        return <p className="text-slate-500">Nessun immobile assegnato</p>;
                      }
                      return (
                        <ul className="space-y-1">
                          {props.slice(0, 5).map(p => (
                            <li key={p.id} className="flex items-center gap-2">
                              <Building2 className="h-3 w-3 text-slate-400" />
                              <span>{p.name}</span>
                              {p.city && <span className="text-slate-400 text-xs">({p.city})</span>}
                            </li>
                          ))}
                          {props.length > 5 && (
                            <li className="text-slate-500 text-xs">...e altri {props.length - 5}</li>
                          )}
                        </ul>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Chiudi
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setShowDetailModal(false);
                    openEditDialog(detailManager);
                  }}
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Modifica
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default PropertyManagersManager;
